import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link2, Loader2, AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/Button';
import { OAuthPlatform, OAUTH_CONNECTORS } from '../../services/oauthConnectors';
import { UserAuthModal } from './UserAuthModal';
interface UserConnectButtonProps {
  platform: OAuthPlatform;
  onSuccess?: () => void;
  className?: string;
}
/**
 * One-click connect button for end users.
 * If the connector exists in OAUTH_CONNECTORS, the user can connect.
 * Uses a simulated OAuth popup flow for demo purposes.
 */
export function UserConnectButton({
  platform,
  onSuccess,
  className = ''
}: UserConnectButtonProps) {
  const [status, setStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const config = OAUTH_CONNECTORS.find(c => c.id === platform);
  // If the platform doesn't exist in our connector registry, don't render
  if (!config) return null;
  const handleConnectClick = () => {
    setIsModalOpen(true);
  };
  const handleAuthorize = () => {
    setIsModalOpen(false);
    setStatus('success');
    if (onSuccess) {
      setTimeout(onSuccess, 1800);
    }
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    setStatus('idle');
  };
  return <>
      <div className={`space-y-2 ${className}`} data-id="element-3085">
        <AnimatePresence mode="wait" data-id="element-3086">
          {status === 'idle' && <motion.div key="idle" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15
        }} data-id="element-3087">
              <Button variant="primary" className="w-full justify-center bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/20 transition-all" onClick={handleConnectClick} data-id="element-3088">
                <Link2 size={16} className="mr-2" data-id="element-3089" />
                Conectar mi cuenta
              </Button>
              <p className="flex items-center justify-center gap-1.5 mt-2 text-[10px] text-slate-500" data-id="element-3090">
                <ShieldCheck size={10} className="text-slate-500" data-id="element-3091" />
                Seguro — no accedemos a tu contraseña
              </p>
            </motion.div>}

          {status === 'connecting' && <motion.div key="connecting" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15
        }} data-id="element-3092">
              <Button variant="primary" className="w-full justify-center cursor-wait" style={{
            background: `linear-gradient(135deg, ${config.brandColor}cc, ${config.brandColor}99)`
          }} disabled data-id="element-3093">
                <Loader2 size={16} className="mr-2 animate-spin" data-id="element-3094" />
                Conectando...
              </Button>
              <p className="text-center mt-2 text-[10px] text-slate-400 animate-pulse" data-id="element-3095">
                Autorizá en la ventana emergente
              </p>
            </motion.div>}

          {status === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20
        }} data-id="element-3096">
              <Button variant="primary" className="w-full justify-center bg-emerald-600 hover:bg-emerald-600 text-white border-none shadow-lg shadow-emerald-900/20" disabled data-id="element-3097">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 15,
              delay: 0.1
            }} data-id="element-3098">
                  <CheckCircle2 size={16} className="mr-2" data-id="element-3099" />
                </motion.div>
                ¡Conectado!
              </Button>
              <p className="text-center mt-2 text-[10px] text-emerald-400" data-id="element-3100">
                Tu cuenta fue vinculada exitosamente
              </p>
            </motion.div>}

          {status === 'error' && <motion.div key="error" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15
        }} data-id="element-3101">
              <Button variant="outline" className="w-full justify-center text-red-400 border-red-900/50 bg-red-950/20 hover:bg-red-950/40" onClick={handleConnectClick} data-id="element-3102">
                <AlertCircle size={16} className="mr-2" data-id="element-3103" />
                Reintentar
              </Button>
              <p className="text-center mt-2 text-[10px] text-red-400/80" data-id="element-3104">
                {errorMsg || 'Algo salió mal. Intentá de nuevo.'}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <UserAuthModal open={isModalOpen} platform={platform} onAuthorize={handleAuthorize} onCancel={handleCancel} data-id="element-3105" />
    </>;
}